import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useDialerContext } from "./DialerContext";
import { useSip } from "./SipContext";
import { useAuth } from "./AuthContext";
import { DEMO_CALLING_OFF } from "../utils/demoWorkspace";
import { toast } from "react-hot-toast";
import callApi from "../services/callApi";
import dialerApi from "../services/dialerApi";
import { addCallListener } from "../services/sipService";
import { isUuid, NATIVE_MEDIA_STATUS } from "../services/nativeMediaService";
import { toCamel } from "../utils/caseConverter";

// Create context
const IntegratedDialerContext = createContext(null);
const TERMINAL_CALL_STATUSES = new Set([
  "completed", "ended", "failed", "no_answer", "busy", "abandoned", "cancelled", "rejected",
]);

/**
 * IntegratedDialerProvider
 *
 * This provider integrates the DialerContext and SipContext to provide a unified
 * interface for call handling, supporting both WebRTC/SIP and server-side Asterisk integration.
 */
export const IntegratedDialerProvider = ({ children }) => {
  // Get contexts
  const dialer = useDialerContext();
  const sip = useSip();
  const demoWorkspace = Boolean(useAuth()?.user?.demo);

  // Local state
  const [statusHistory, setStatusHistory] = useState([]);
  const [callQualityMetrics, setCallQualityMetrics] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingData, setRecordingData] = useState(null);
  const pendingOutboundCallIdRef = useRef(null);
  // Last observed SIP status, including while unregistered. A stale ended/failed
  // value must not look like a new terminal transition when registration returns.
  const observedSipCallStatusRef = useRef(null);
  const settledNativeCallIdRef = useRef(null);

  const finishNativeCall = useCallback((record) => {
    const info = toCamel(record);
    const predictive = info.dialingMode === "predictive";
    if (predictive && typeof info.predictiveAgentConnected !== "boolean") return false;
    if (settledNativeCallIdRef.current === info.id) return true;
    settledNativeCallIdRef.current = info.id;
    pendingOutboundCallIdRef.current = null;
    dialer.setCurrentCallInfo(null);
    dialer.setIsMuted(false);
    dialer.setIsOnHold?.(false);
    dialer.updateAgentStatus(
      predictive && !info.predictiveAgentConnected ? "available" : "Wrap-up",
      { persist: !predictive },
    );
    return true;
  }, [dialer.setCurrentCallInfo, dialer.setIsMuted, dialer.setIsOnHold, dialer.updateAgentStatus]);

  // The canonical POST /calls response selects the call backend. Native media
  // controls must remain available while the realtime socket reconnects; the
  // channel session is intentionally recoverable from this tracked call.
  const currentCallId =
    dialer.currentCallInfo?.id || dialer.currentCallInfo?.callId || null;
  const isUsingNativeMedia = Boolean(
    dialer.currentCallInfo?.backend === "native_sip" && isUuid(currentCallId),
  );
  const isUsingSip =
    sip.isRegistered && sip.isInitialized && !isUsingNativeMedia;
  // JsSIP's duration events do not fire for native calls. Measure elapsed
  // connected time per tracked call, retaining its anchor through hold/rejoin.
  const nativeClockRef = useRef({ callId: null, startedAt: null });
  const [nativeDuration, setNativeDuration] = useState({ callId: null, seconds: 0 });
  const nativeAnsweredAt = dialer.currentCallInfo?.answeredAt || dialer.currentCallInfo?.answered_at;
  const nativeClockEnded = TERMINAL_CALL_STATUSES.has(dialer.currentCallInfo?.status);
  const nativeClockConnected = dialer.currentCallInfo?.status === "live" &&
    sip.nativeMediaStatus === NATIVE_MEDIA_STATUS.CONNECTED &&
    (dialer.currentCallInfo?.dialingMode !== "predictive" ||
      dialer.currentCallInfo?.predictiveAgentConnected === true);

  useEffect(() => {
    if (!isUsingNativeMedia || nativeClockEnded) {
      nativeClockRef.current = { callId: null, startedAt: null };
      setNativeDuration((previous) => previous.callId === null && previous.seconds === 0
        ? previous : { callId: null, seconds: 0 });
      return undefined;
    }

    if (nativeClockRef.current.callId !== currentCallId) {
      nativeClockRef.current = { callId: currentCallId, startedAt: null };
    }
    const clock = nativeClockRef.current;
    if (clock.startedAt === null && nativeClockConnected) {
      const answeredAt = Date.parse(nativeAnsweredAt);
      clock.startedAt = Number.isFinite(answeredAt) ? Math.min(Date.now(), answeredAt) : Date.now();
    }
    const update = () => {
      const seconds = clock.startedAt === null ? 0 : Math.max(0, Math.floor((Date.now() - clock.startedAt) / 1000));
      setNativeDuration((previous) => previous.callId === currentCallId && previous.seconds === seconds
        ? previous : { callId: currentCallId, seconds });
    };
    update();
    if (clock.startedAt === null) return undefined;
    const interval = window.setInterval(update, 1000);
    return () => window.clearInterval(interval);
  }, [currentCallId, isUsingNativeMedia, nativeAnsweredAt, nativeClockConnected, nativeClockEnded]);
  useEffect(() => {
    const callStatus = sip.callStatus;
    const previousStatus = observedSipCallStatusRef.current;
    const isTerminal = callStatus === "ended" || callStatus === "failed";
    const enteredTerminal = isTerminal && previousStatus === null;
    // Record before the SIP gate so isRegistered false→true with a stale
    // terminal status is not a new transition. ended→failed stays terminal
    // and must not publish again. Nonterminal statuses clear the marker so
    // a later distinct call can enter wrap-up.
    observedSipCallStatusRef.current = isTerminal ? callStatus : null;

    if (!isUsingSip) return;

    const sipCallId = sip.callInfo?.id || sip.callInfo?.callId || null;
    const customerConnected = [
      "active",
      "customer_answered",
      "customerAnswered",
    ].includes(callStatus);
    const connecting = [
      "outgoing",
      "incoming",
      "progress",
      "agent_ringing",
      "agent_answered",
      "agent_connected",
      "customer_dialing",
    ].includes(callStatus);

    if (customerConnected) {
      dialer.updateAgentStatus("On Call");
    } else if (connecting) {
      dialer.updateAgentStatus("Connecting");
    } else if (enteredTerminal) {
      pendingOutboundCallIdRef.current = null;
      dialer.updateAgentStatus("Wrap-up");
    }

    if (sip.callInfo) {
      dialer.setCurrentCallInfo((current) => {
        if (
          current?.id &&
          (!sipCallId || String(current.id) !== String(sipCallId))
        ) {
          return current;
        }

        return {
          ...(current || {}),
          ...(sipCallId ? { id: sipCallId, callId: sipCallId } : {}),
          direction: sip.callInfo.direction,
          name: current?.name || sip.callInfo.displayName || "Unknown caller",
          number:
            current?.number ||
            sip.callInfo.from ||
            sip.callInfo.to ||
            "Unknown",
          phase: sip.callInfo.phase || sip.callStatus,
          status: customerConnected ? "live" : "connecting",
          ...(!sipCallId ? { untrackedSip: true } : {}),
        };
      });
    } else if (sip.callStatus === "ended" || sip.callStatus === "failed") {
      dialer.setCurrentCallInfo(null);
    }

    if (sip.isMuted !== dialer.isMuted) {
      dialer.setIsMuted(sip.isMuted);
    }
  }, [sip.callStatus, sip.callInfo, sip.isMuted, isUsingSip]);

  useEffect(() => {
    const info = sip.nativeCallInfo;
    if (!info) return;

    const phase = info.phase || "awaiting_media";
    const predictive = info.dialingMode === "predictive";
    const customerConnected = ["active", "customer_answered", "connected", "on_hold"].includes(phase) &&
      sip.nativeMediaStatus === NATIVE_MEDIA_STATUS.CONNECTED &&
      (!predictive || info.predictiveAgentConnected === true);
    const mediaEnded = sip.nativeMediaStatus === NATIVE_MEDIA_STATUS.ENDED;

    if (mediaEnded || phase === "ended" || TERMINAL_CALL_STATUSES.has(info.status)) {
      finishNativeCall(info);
      return;
    }

    dialer.updateAgentStatus(customerConnected ? "On Call" : "Connecting", { persist: !predictive });
    dialer.setCurrentCallInfo((current) => {
      if (current?.id && String(current.id) !== String(info.id)) return current;
      return {
        ...(current || {}),
        id: info.id,
        callId: info.id,
        backend: "native_sip",
        direction: info.direction,
        number:
          current?.number ||
          info.destinationNum ||
          info.callerIdNum ||
          "Unknown",
        name: current?.name || info.callerIdName || "Unknown caller",
        phase,
        mediaStatus: sip.nativeMediaStatus,
        answeredAt: info.answeredAt || current?.answeredAt || null,
        dialingMode: info.dialingMode,
        predictiveAgentConnected: info.predictiveAgentConnected,
        status: customerConnected ? "live" : "connecting",
      };
    });
  }, [sip.nativeCallInfo, sip.nativeMediaStatus, finishNativeCall]);

  useEffect(() => {
    if (
      !isUsingNativeMedia ||
      ![NATIVE_MEDIA_STATUS.FAILED, NATIVE_MEDIA_STATUS.ENDED].includes(sip.nativeMediaStatus) ||
      !isUuid(currentCallId)
    ) {
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let attemptsRemaining = 60;

    const resyncTerminalCall = async () => {
      try {
        const response = await callApi.getById(currentCallId);
        const record = toCamel(response?.data || response);
        const recordId = record?.id || record?.callId;
        const status = String(record?.status || "").toLowerCase();

        if (
          isUuid(recordId) &&
          recordId.toLowerCase() === currentCallId.toLowerCase() &&
          record?.backend === "native_sip" &&
          TERMINAL_CALL_STATUSES.has(status)
        ) {
          if (finishNativeCall({ ...record, id: recordId.toLowerCase() })) {
            cancelled = true;
            sip.closeNativeMedia();
            return;
          }
        }
      } catch (_) {
        // The authenticated call channel remains primary. A transient REST
        // resync failure is retried without claiming a terminal call state.
      }

      attemptsRemaining -= 1;
      if (!cancelled && attemptsRemaining > 0) {
        timer = window.setTimeout(resyncTerminalCall, 1000);
      }
    };

    void resyncTerminalCall();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    currentCallId,
    dialer.setCurrentCallInfo,
    dialer.setIsMuted,
    dialer.setIsOnHold,
    dialer.updateAgentStatus,
    finishNativeCall,
    isUsingNativeMedia,
    sip.closeNativeMedia,
    sip.nativeMediaStatus,
  ]);
  // Integrated call functions
  const startCall = useCallback(
    async (phoneNumber, callMetadata = {}) => {
      // The demo workspace never places calls (DemoGuard on the API). Say so
      // once, as information, instead of letting the request fail.
      if (demoWorkspace) {
        toast(DEMO_CALLING_OFF, { id: "demo-calling-off" });
        return false;
      }
      if (!phoneNumber) {
        toast.error("Please enter a phone number");
        return false;
      }

      if (!callMetadata.contactId) {
        toast.error("Select a campaign contact before starting a call");
        return false;
      }

      if (typeof phoneNumber === "number") {
        phoneNumber = phoneNumber.toString();
      }
      if (
        typeof phoneNumber === "string" &&
        !phoneNumber.startsWith("+1") &&
        phoneNumber.length === 10
      ) {
        phoneNumber = `+1${phoneNumber}`;
      }

      try {
        dialer.updateAgentStatus("Connecting");
        const callRecord = await callApi.initiate({
          contactId: callMetadata.contactId,
          campaignId: callMetadata.campaignId,
        });
        const record = callRecord?.data || callRecord;
        const callId = record?.id || record?.callId || record?.call_id;
        if (
          !isUuid(callId) ||
          !["asterisk", "native_sip"].includes(record?.backend) ||
          record?.direction !== "outbound" ||
          (record?.backend === "native_sip" &&
            record?.phase !== "awaiting_media")
        ) {
          throw new Error(
            "The server returned an invalid call routing response.",
          );
        }

        const canonicalCallId = callId.toLowerCase();
        const nativeCall =
          record.backend === "native_sip" &&
          record.phase === "awaiting_media" &&
          record.direction === "outbound";
        pendingOutboundCallIdRef.current = nativeCall ? null : canonicalCallId;

        dialer.startCall(phoneNumber, {
          ...callMetadata,
          callId: canonicalCallId,
          backend: record.backend,
          phase: record.phase,
          direction: record.direction,
          mediaStatus: nativeCall ? NATIVE_MEDIA_STATUS.WAITING : undefined,
          status: "connecting",
        });

        if (nativeCall) {
          try {
            // Joining exchanges identifiers and ephemeral ICE credentials only.
            // Audio capture/offer creation is reserved for Connect Audio.
            await sip.prepareNativeCall({
              ...record,
              id: canonicalCallId,
            });
          } catch (error) {
            toast.error(error.message || "Native call audio is not ready.");
          }
        }
        return true;
      } catch (error) {
        pendingOutboundCallIdRef.current = null;
        toast.error(error.message || "Failed to initiate call");
        dialer.updateAgentStatus("available");
        return false;
      }
    },
    [demoWorkspace, dialer, sip.prepareNativeCall],
  );

  const connectNativeAudio = useCallback(async () => {
    const current = dialer.currentCallInfo;
    const callId = current?.id || current?.callId;
    if (
      current?.backend !== "native_sip" ||
      !isUuid(callId) ||
      !["inbound", "outbound"].includes(current?.direction)
    ) {
      return sip.connectNativeAudio();
    }

    const preparedCallId = sip.nativeCallInfo?.id || sip.nativeCallInfo?.callId;
    if (
      isUuid(preparedCallId) &&
      preparedCallId.toLowerCase() === callId.toLowerCase()
    ) {
      return sip.connectNativeAudio();
    }

    return sip.prepareNativeCall(
      {
        ...current,
        id: callId.toLowerCase(),
        callId: callId.toLowerCase(),
      },
      { connectAudio: true, rejoin: true },
    );
  }, [
    dialer.currentCallInfo,
    sip.connectNativeAudio,
    sip.nativeCallInfo,
    sip.prepareNativeCall,
  ]);

  useEffect(() => {
    if (sip.callStatus !== "incoming" || !pendingOutboundCallIdRef.current) {
      return;
    }

    const incomingCallId = sip.callInfo?.id || sip.callInfo?.callId;
    if (
      !isUuid(incomingCallId) ||
      incomingCallId.toLowerCase() !== pendingOutboundCallIdRef.current
    ) {
      return;
    }

    if (sip.answerCall()) {
      pendingOutboundCallIdRef.current = null;
    }
  }, [sip.callInfo, sip.callStatus, sip.answerCall]);

  const endCall = useCallback(async () => {
    if (isUsingNativeMedia) {
      const ended = await sip.hangupNativeCall();
      if (!ended) return false;

      stopCallQualityMonitoring();
      if (isRecording) stopRecording();
      finishNativeCall({ ...dialer.currentCallInfo, ...sip.nativeCallInfo, ...ended });
      return true;
    }

    pendingOutboundCallIdRef.current = null;
    stopCallQualityMonitoring();
    if (isRecording) stopRecording();
    const result = await dialer.endCall();
    return result !== false;
  }, [dialer, finishNativeCall, isRecording, isUsingNativeMedia, sip.hangupNativeCall, sip.nativeCallInfo]);

  const toggleMute = useCallback(async () => {
    if (isUsingNativeMedia) {
      const next = !dialer.isMuted;
      const changed = await sip.nativeControl(next ? "mute" : "unmute");
      if (!changed) return dialer.isMuted;
      dialer.setIsMuted(next);
      return next;
    }
    if (isUsingSip && sip.isInCall()) {
      const next = sip.toggleMute();
      dialer.setIsMuted(next);
      return next;
    }
    return dialer.toggleMute();
  }, [dialer, isUsingNativeMedia, isUsingSip, sip]);

  const holdCall = useCallback(async () => {
    if (!isUsingNativeMedia) return dialer.holdCall?.();
    const held = await sip.nativeControl("hold");
    if (held) dialer.setIsOnHold?.(true);
    return held;
  }, [dialer, isUsingNativeMedia, sip.nativeControl]);

  const unholdCall = useCallback(async () => {
    if (!isUsingNativeMedia) return dialer.unholdCall?.();
    const resumed = await sip.nativeControl("resume");
    if (resumed) dialer.setIsOnHold?.(false);
    return resumed;
  }, [dialer, isUsingNativeMedia, sip.nativeControl]);

  // Blind transfer — prefers the backend REST endpoint which routes through
  // whichever backend (native SIP / Asterisk) is currently active.
  const transferCall = useCallback(
    async (extension, context = "from-internal") => {
      const callId =
        dialer.currentCallInfo?.id || dialer.currentCallInfo?.callId;
      if (!callId) {
        toast.error("No active call to transfer");
        return false;
      }
      if (!extension || typeof extension !== "string") {
        toast.error("Transfer extension is required");
        return false;
      }
      try {
        await dialerApi.transferCall(String(callId), extension.trim(), context);
        // Success toast is driven by the PubSub `transferred` broadcast so
        // the destination echoed back from the backend is authoritative.
        return true;
      } catch (err) {
        toast.error(err.message || "Failed to transfer call");
        return false;
      }
    },
    [dialer.currentCallInfo],
  );

  // Recent DTMF indicator — last 5 digits, auto-clears 3s after the most
  // recent digit. Populated by sipService call listener which receives
  // both JsSIP in-band DTMF and the {:dtmf, digit} PubSub broadcast fed
  // through SipContext → notifyFsmListeners.
  const [recentDtmf, setRecentDtmf] = useState([]);
  const dtmfClearTimer = useRef(null);

  const pushRecentDtmf = useCallback((digit) => {
    if (!digit) return;
    setRecentDtmf((prev) => [...prev, digit].slice(-5));
    if (dtmfClearTimer.current) clearTimeout(dtmfClearTimer.current);
    dtmfClearTimer.current = setTimeout(() => setRecentDtmf([]), 3000);
  }, []);

  // Send DTMF — prefers WebRTC in-band tones, falls back to backend REST
  // which emits a PubSub broadcast so the "recent DTMF" indicator stays
  // accurate regardless of the path.
  const sendDTMFIntegrated = useCallback(
    async (digit) => {
      const d = typeof digit === "string" ? digit : String(digit ?? "");
      if (!d) return false;
      if (isUsingNativeMedia) {
        const sent = await sip.nativeControl("dtmf", { digit: d });
        if (sent) pushRecentDtmf(d);
        return sent;
      }
      if (isUsingSip && sip.isInCall()) {
        try {
          sip.sendDTMF(d);
        } catch (_) {}
      }
      const callId =
        dialer.currentCallInfo?.id || dialer.currentCallInfo?.callId;
      if (callId) {
        try {
          await dialerApi.sendDTMF(String(callId), d);
        } catch (_) {
          // DTMF failure remains non-modal for the registered endpoint.
        }
      }
      return true;
    },
    [
      dialer.currentCallInfo,
      isUsingNativeMedia,
      isUsingSip,
      pushRecentDtmf,
      sip,
    ],
  );

  useEffect(() => {
    const off = addCallListener((event, data) => {
      if (event === "dtmf") {
        pushRecentDtmf(typeof data === "string" ? data : data?.digit || "");
      } else if (event === "transferred") {
        const ext = typeof data === "string" ? data : data?.extension || "";
        toast.success(ext ? `Transferred to ${ext}` : "Call transferred");
        // Close the active call panel — the FSM :ended event will normally
        // tear this down, but transfers can race the backend so we clear
        // proactively here.
        dialer.setCurrentCallInfo?.(null);
      } else if (event === "muted") {
        // sipService `muted` event carries a boolean (true=muted/false=unmuted)
        // from both JsSIP and the backend PubSub forwarder in SipContext.
        const next = typeof data === "boolean" ? data : true;
        dialer.setIsMuted?.(next);
      } else if (event === "unmuted") {
        dialer.setIsMuted?.(false);
      } else if (event === "on_hold") {
        dialer.setIsOnHold?.(true);
      } else if (event === "active" || event === "unhold") {
        dialer.setIsOnHold?.(false);
      }
    });
    return () => {
      if (off) off();
      if (dtmfClearTimer.current) clearTimeout(dtmfClearTimer.current);
    };
  }, [pushRecentDtmf, dialer]);

  // Call quality monitoring with real WebRTC stats
  const startCallQualityMonitoring = useCallback(() => {
    if (!isUsingSip || !sip.isInCall()) return;

    // Initialize metrics
    setCallQualityMetrics({
      jitter: 0,
      packetsLost: 0,
      roundTripTime: 0,
      timestamp: Date.now(),
    });

    // Get the current RTCPeerConnection from JsSIP
    const getRTCPeerConnection = () => {
      if (!sip.sipContext || !sip.sipContext.currentSession) return null;
      return sip.sipContext.currentSession.connection;
    };

    // Set up interval to update metrics
    const intervalId = setInterval(async () => {
      if (!sip.isInCall()) {
        clearInterval(intervalId);
        return;
      }

      const pc = getRTCPeerConnection();
      if (!pc) return;

      try {
        // Get WebRTC stats
        const stats = await pc.getStats();
        let jitter = 0;
        let packetsLost = 0;
        let roundTripTime = 0;

        // Process stats
        stats.forEach((report) => {
          if (report.type === "inbound-rtp" && report.kind === "audio") {
            jitter = report.jitter ? report.jitter * 1000 : 0; // Convert to ms
            packetsLost = report.packetsLost || 0;
          }

          if (
            report.type === "candidate-pair" &&
            report.state === "succeeded"
          ) {
            roundTripTime = report.currentRoundTripTime
              ? report.currentRoundTripTime * 1000
              : 0; // Convert to ms
          }
        });

        // Update metrics
        setCallQualityMetrics({
          jitter,
          packetsLost,
          roundTripTime,
          timestamp: Date.now(),
        });
      } catch (error) {}
    }, 2000);

    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [isUsingSip, sip]);

  const stopCallQualityMonitoring = useCallback(() => {
    setCallQualityMetrics(null);
  }, []);

  // Call recording with MediaRecorder API
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordedChunks, setRecordedChunks] = useState([]);

  const startRecording = useCallback(async () => {
    if (isUsingNativeMedia) {
      if (!sip.nativeMedia?.capabilities?.recording) {
        toast.error("Recording is not enabled for this native call");
        return false;
      }
      if (dialer.currentCallInfo?.status !== "live") {
        toast.error("Recording starts only after the customer is connected");
        return false;
      }
      try {
        const reply = await sip.startNativeRecording();
        setRecordingData({
          startTime: Date.now(),
          callId: reply.call_id,
          recordingUrl: null,
          transcription: null,
        });
        toast.success("Call recording started");
        return true;
      } catch (error) {
        toast.error(error.message || "Failed to start native call recording");
        return false;
      }
    }

    if (!isUsingSip || !sip.isInCall()) {
      toast.error("Cannot start recording: No active call");
      return false;
    }

    const getAudioStream = () => {
      if (!sip.sipContext || !sip.sipContext.currentSession) return null;

      const remoteAudio =
        document.querySelector("audio#remote-audio") ||
        document.querySelector('audio[id*="remote"]') ||
        sip.sipContext.audioRef?.current;

      if (!remoteAudio || !remoteAudio.srcObject) {
        return null;
      }

      return remoteAudio.srcObject;
    };

    try {
      const stream = getAudioStream();
      if (!stream) {
        toast.error("Cannot access call audio stream");
        return false;
      }

      const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      setMediaRecorder(recorder);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setRecordedChunks((chunks) => [...chunks, event.data]);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunks, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        document.body.appendChild(a);
        a.style.display = "none";
        a.href = url;

        const phoneNumber = sip.callInfo?.from || sip.callInfo?.to || "unknown";
        const date = new Date().toISOString().replace(/[:.]/g, "-");
        a.download = `call-recording-${phoneNumber}-${date}.webm`;
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setRecordedChunks([]);
      };

      recorder.start(1000);
      setIsRecording(true);
      setRecordingData({
        startTime: Date.now(),
        callId: sip.callInfo?.id || "unknown",
        phoneNumber: sip.callInfo?.from || sip.callInfo?.to || "unknown",
      });
      toast.success("Call recording started");
      return true;
    } catch (error) {
      toast.error(`Failed to start recording: ${error.message}`);
      return false;
    }
  }, [
    dialer.currentCallInfo,
    isUsingNativeMedia,
    isUsingSip,
    recordedChunks,
    sip,
  ]);

  const stopRecording = useCallback(async () => {
    if (isUsingNativeMedia) {
      try {
        const reply = await sip.stopNativeRecording();
        setRecordingData((prev) => ({
          ...prev,
          endTime: Date.now(),
          recordingUrl: reply.recording_url,
          transcription: reply.transcription,
        }));
        toast.success("Call recording saved; transcription queued");
        return true;
      } catch (error) {
        toast.error(error.message || "Failed to stop native call recording");
        return false;
      }
    }

    if (!isRecording || !mediaRecorder) return false;

    try {
      mediaRecorder.stop();
      setIsRecording(false);
      setRecordingData((prev) => ({
        ...prev,
        endTime: Date.now(),
        duration: prev ? (Date.now() - prev.startTime) / 1000 : 0,
      }));
      toast.success("Call recording stopped and saved");
      return true;
    } catch (error) {
      toast.error(`Failed to stop recording: ${error.message}`);
      return false;
    }
  }, [isRecording, isUsingNativeMedia, mediaRecorder, sip]);

  // Context value
  const value = {
    statusHistory,
    setStatusHistory,
    // State
    ...dialer,
    isUsingSip,
    isUsingNativeMedia,
    isInCall: isUsingNativeMedia
      ? dialer.currentCallInfo?.status === "live"
      : dialer.isInCall,
    callDuration: isUsingNativeMedia
      ? (nativeDuration.callId === currentCallId ? nativeDuration.seconds : 0)
      : dialer.callDuration,
    nativeCallInfo: sip.nativeCallInfo,
    nativeMedia: sip.nativeMedia,
    nativeMediaStatus: sip.nativeMediaStatus,
    nativeMediaError: sip.nativeMediaError,
    nativeSignalingReady: sip.isNativeSignalingReady,
    callQualityMetrics,
    nativeRecordingAvailable: Boolean(
      isUsingNativeMedia && sip.nativeMedia?.capabilities?.recording,
    ),
    recordingPending: isUsingNativeMedia
      ? Boolean(sip.nativeMedia?.recordingPending)
      : false,
    recordingError: isUsingNativeMedia ? sip.nativeMedia?.recordingError : null,
    isRecording: isUsingNativeMedia
      ? Boolean(sip.nativeMedia?.recording)
      : isRecording,
    recordingData: isUsingNativeMedia
      ? {
          ...(recordingData || {}),
          recordingUrl: sip.nativeMedia?.recordingUrl || null,
          transcription: sip.nativeMedia?.transcription || null,
        }
      : recordingData,
    recentDtmf,

    // Override dialer functions with integrated versions
    startCall,
    endCall,
    toggleMute,
    sendDTMF: sendDTMFIntegrated,
    holdCall,
    unholdCall,
    transferCall,

    // Native media remains staged until this explicit owner gesture.
    connectNativeAudio,
    prepareNativeCall: sip.prepareNativeCall,
    startRecording,
    stopRecording,

    // Expose original contexts for advanced usage
    dialerContext: dialer,
    sipContext: sip,
    callStatus: isUsingNativeMedia
      ? sip.nativeCallInfo?.phase || "awaiting_media"
      : sip.callStatus,
  };

  return (
    <IntegratedDialerContext.Provider value={value}>
      {children}
    </IntegratedDialerContext.Provider>
  );
};

// Custom hook to use the integrated dialer context
export const useIntegratedDialer = () => {
  const context = useContext(IntegratedDialerContext);
  if (!context) {
    throw new Error(
      "useIntegratedDialer must be used within an IntegratedDialerProvider",
    );
  }
  return context;
};

export default IntegratedDialerContext;
